import { decoded, openOutput, play, wakeOnGestures } from "./fuxi-audio.js";
import {
  feedbackSoundAddress,
  feedbackSoundsBase,
} from "../user/fuxi-sound-addresses.js";

/**
 * The right and wrong notes 复习 marks a card with, for an Exercise that
 * marks an answer the same way.
 *
 * A grammar exercise (`./lianxi-yufa-feedback.ts`) and a reading Exercise
 * (`./lianxi-yuedu-start.ts`) both hear their verdicts through this. The files
 * are 复习's own, from `../user/fuxi-sound-addresses.ts`, played through the
 * output `./fuxi-audio.ts` builds, so an Exercise sounds like a card and
 * nothing is copied. Both files are fetched and decoded as the page loads, and
 * the output wakes on the reader's first gesture, for the reason
 * `./fuxi-sounds.ts` gives.
 *
 * A browser with no Web Audio, or a file that cannot be fetched, leaves the
 * Exercise silent and marking as before. The verdict is in ink either way.
 */

/** Play the note for one verdict: right, or wrong. */
export type HearVerdict = (right: boolean) => void;

/** How loudly a note plays, as a grammar exercise has always played it. */
const volume = 0.25;

/**
 * Load the two notes, and say how to play them.
 * @param page - The page the Exercise is on, whose gestures wake the output.
 * @param base - Where the notes are served from.
 * @returns What plays a verdict's note, or does nothing where none can play.
 */
export function verdictSounds(
  page: Document,
  base: string = feedbackSoundsBase,
): HearVerdict {
  const output = openOutput();

  if (output === undefined) {
    return () => undefined;
  }

  wakeOnGestures(page, output);

  const notes = new Map<string, AudioBuffer>();

  for (const tone of ["right", "wrong"] as const) {
    void decoded(output, feedbackSoundAddress(base, tone)).then((buffer) => {
      if (buffer !== undefined) {
        notes.set(tone, buffer);
      }
    });
  }

  return (right) => {
    const buffer = notes.get(right ? "right" : "wrong");

    if (buffer === undefined) {
      return;
    }

    if (output.state !== "running") {
      void output.resume().catch(() => undefined);
    }

    play({ output, buffer, at: output.currentTime, gain: volume });
  };
}
